import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useAtom, useSetAtom } from "jotai";
import type { UseFormReturn } from "react-hook-form";
import {
  designSubTabAtom,
  designSubTabInitializedAtom
} from "@design-lab/shared/lib/tab-navigation";
import type { ArtworkForm } from "@design-lab/shared/types";

/**
 * Hook to initialize the design sub-tab based on URL parameters or loaded project data.
 * Only active when the A/B test variant reordered tabs is enabled.
 *
 */
type UseDesignSubTabInitializationProps = {
  showSubTabs: boolean;
  artworkForm: UseFormReturn<ArtworkForm> | null;
};

export function useDesignSubTabInitialization(props: UseDesignSubTabInitializationProps): void {
  const { showSubTabs, artworkForm } = props;
  const [searchParams] = useSearchParams();
  const setDesignSubTab = useSetAtom(designSubTabAtom);
  const [hasInitialized, setHasInitialized] = useAtom(designSubTabInitializedAtom);

  // Early return: Do nothing if not in A/B test variant
  const isExperimentActive = showSubTabs;

  const tabFromURL = searchParams.get("tab");
  const intendedSideRef = useRef<"front" | "back" | null>(null);

  // Track form values to trigger re-checks when they change
  const [formValuesSnapshot, setFormValuesSnapshot] = useState<string>("");

  // Subscribe to form changes to detect when project data is loaded
  // Only runs for A/B test variant users
  useEffect(() => {
    if (!isExperimentActive || !artworkForm) return undefined;

    const subscription = artworkForm.watch(values => {
      // Create a snapshot of relevant artwork IDs to trigger re-initialization check
      const snapshot = JSON.stringify({
        front: values?.front?.artworkId,
        back: values?.back?.artworkId,
        frontLeftChest: values?.frontLeftChest?.artworkId
      });
      setFormValuesSnapshot(snapshot);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [artworkForm, isExperimentActive]);

  useEffect(() => {
    if (!isExperimentActive) return;

    // Only initialize once per project - don't override manual user selections
    if (hasInitialized) return;

    // Check multiple sources for the intended side
    const sideParam = searchParams.get("side");
    const uploadArtworkUrls = searchParams.get("upload_urls");
    let intendedSide: "front" | "back" | null = null;
    let shouldMarkAsInitialized = false;

    // Direct side parameter (from external sources like PLP)
    if (sideParam === "front" || sideParam === "back") {
      intendedSide = sideParam;
      shouldMarkAsInitialized = true;
    }
    // upload_urls parameter (external uploads)
    else if (uploadArtworkUrls && !tabFromURL) {
      try {
        const urlsObj = JSON.parse(uploadArtworkUrls) as Record<string, string>;
        if (urlsObj?.["front"]) {
          intendedSide = "front";
        } else if (urlsObj?.["back"]) {
          intendedSide = "back";
        }
        shouldMarkAsInitialized = true;
      } catch {
        // ignore malformed upload_urls and fall back to default initialization behavior
      }
    }
    // Editing existing project - detect from loaded artwork
    else if (!sideParam && !uploadArtworkUrls) {
      if (!artworkForm) return;

      const formValues = artworkForm.getValues();
      const frontArtworkId = formValues?.front?.artworkId;
      const frontLeftChestArtworkId = formValues?.frontLeftChest?.artworkId;
      const backArtworkId = formValues?.back?.artworkId;

      const hasFrontArtwork = !!(frontArtworkId || frontLeftChestArtworkId);
      const hasBackArtwork = !!backArtworkId;

      if (hasFrontArtwork || hasBackArtwork) {
        // Initialize to the side that has artwork
        // If both have artwork, prefer front; if only back has artwork, use back
        if (hasBackArtwork && !hasFrontArtwork) {
          intendedSide = "back";
        } else if (hasFrontArtwork) {
          intendedSide = "front";
        }
        shouldMarkAsInitialized = true;
      } else if (formValuesSnapshot) {
        // Form is loaded but no artwork found - mark as initialized to prevent further checks
        shouldMarkAsInitialized = true;
      }
    } else {
      // No special initialization needed - use default
      shouldMarkAsInitialized = true;
    }

    if (intendedSide && intendedSide !== intendedSideRef.current) {
      intendedSideRef.current = intendedSide;
      setDesignSubTab(intendedSide);
    }

    if (shouldMarkAsInitialized) {
      setHasInitialized(true);
    }
  }, [
    isExperimentActive,
    searchParams,
    tabFromURL,
    setDesignSubTab,
    artworkForm,
    formValuesSnapshot,
    hasInitialized,
    setHasInitialized
  ]);
}
